import { useEffect, useRef } from 'react';
import L from 'leaflet';
import boundsKey from 'features/Responder/features/Tours/features/TourMap/utils/boundsKey';

/**
 * Fit the map to the current route or stops when geometry changes.
 * Preserves user pan/zoom when nothing changed meaningfully.
 */
export function useFitBounds({
	mapRef,
	routePoints,
	tripStopLatLngs,
	stopsSignature,
	mapReadyKey = null,
	suppressNextFitRef = null,
}) {
	const lastFitKeyRef = useRef(null);
	const lastFitModeRef = useRef(null);
	const lastFitStopsRef = useRef(null);

	useEffect(() => {
		const map = mapRef.current;
		if (!map) return;
		try {
			const mode = routePoints.length
				? 'route'
				: tripStopLatLngs.length
				? 'stops'
				: null;
			let targetBounds = null;
			if (routePoints.length) {
				targetBounds = L.latLngBounds(routePoints);
			} else if (tripStopLatLngs.length) {
				targetBounds = L.latLngBounds(tripStopLatLngs);
			} else {
				lastFitKeyRef.current = null;
				return;
			}

			if (!targetBounds?.isValid?.()) {
				lastFitModeRef.current = mode;
				lastFitKeyRef.current = null;
				return;
			}

			const round5 = (value) =>
				Number.isFinite(value) ? Math.round(value * 1e5) / 1e5 : 0;
			const routeSignature = (() => {
				if (!routePoints.length) return '';
				const first = routePoints[0] || [];
				const last = routePoints[routePoints.length - 1] || [];
				const mid = routePoints[Math.floor(routePoints.length / 2)] || [];
				return `${round5(first[0])},${round5(first[1])}|${round5(mid[0])},${round5(
					mid[1]
				)}|${round5(last[0])},${round5(last[1])}`;
			})();

			let fitKey = `${mode || 'none'}:${routePoints.length || 0}:${
				tripStopLatLngs.length
			}`;
			if (routePoints.length) {
				const first = routePoints[0] || [];
				const last = routePoints[routePoints.length - 1] || [];
				fitKey += `:start:${first[0]},${first[1]}:end:${last[0]},${last[1]}`;
			} else if (tripStopLatLngs.length) {
				const stopsKey = tripStopLatLngs
					.map((p) => `${p.lat},${p.lng}`)
					.join('|');
				fitKey += `:stopsKey:${stopsKey}`;
			}
			fitKey += `:bounds:${boundsKey(targetBounds)}`;
			if (routeSignature) {
				fitKey += `:routeSig:${routeSignature}`;
			}

			if (suppressNextFitRef?.current) {
				suppressNextFitRef.current = false;
				lastFitKeyRef.current = fitKey;
				lastFitModeRef.current = mode;
				lastFitStopsRef.current = stopsSignature;
				return;
			}

			const forceFit = mode !== lastFitModeRef.current;
			const sameFitKey = fitKey === lastFitKeyRef.current;
			const sameStopsAsLast =
				stopsSignature &&
				stopsSignature === lastFitStopsRef.current &&
				mode === lastFitModeRef.current;

			if ((sameStopsAsLast || sameFitKey) && !forceFit) {
				return;
			}

			if (routePoints.length) {
				// Keep the user's viewport stable while the route rebuilds.
				// This prevents zoom jumps when adding/editing checkpoints.
				const disableRouteAutoFit = true;
				if (!disableRouteAutoFit) {
					map.fitBounds(targetBounds, { padding: [80, 80], maxZoom: 17 });
				}
			} else if (tripStopLatLngs.length === 1) {
				map.setView(
					[tripStopLatLngs[0].lat, tripStopLatLngs[0].lng],
					Math.min(Math.max(map.getZoom(), 12), 17),
					{
						animate: true,
					}
				);
			} else {
				map.fitBounds(targetBounds, { padding: [80, 80], maxZoom: 17 });
			}

			lastFitKeyRef.current = fitKey;
			lastFitModeRef.current = mode;
			lastFitStopsRef.current = stopsSignature;
		} catch (err) {
			// ignore
		}
	}, [mapRef, routePoints, stopsSignature, tripStopLatLngs, mapReadyKey, suppressNextFitRef]);
}
